import React, { useState, useRef, useCallback, useMemo, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import cn from 'classnames';
import { IoIosArrowDown, IoIosArrowUp, IoIosArrowForward } from 'react-icons/io';
import { IoWarningOutline } from 'react-icons/io5';

// Отступ уровня в дереве опций (px)
const TREE_INDENT = 16;

const nodeKey = (parentKey, node, index) => `${parentKey}/${node?.value ?? node?.name ?? index}`;

const nodeChildren = (node) => (Array.isArray(node?.children) && node.children.length ? node.children : null);

// Видимые строки дерева: узел + уровень + раскрыт/нет. Дети попадают в список,
// только если родитель раскрыт — как на странице «МКБ-10» (классы → блоки → диагнозы).
const buildTreeRows = (nodes, expandedKeys, level = 0, parentKey = '', out = []) => {
    (nodes || []).forEach((node, index) => {
        const key = nodeKey(parentKey, node, index);
        const children = nodeChildren(node);
        const expanded = !!children && expandedKeys.has(key);
        out.push({ ...node, __key: key, __level: level, __hasChildren: !!children, __expanded: expanded });
        if (expanded) buildTreeRows(children, expandedKeys, level + 1, key, out);
    });
    return out;
};

// Все листья дерева с текстом для поиска: путь предков + собственное имя.
// Поиск идёт по всему справочнику независимо от того, что сейчас раскрыто.
const collectTreeLeaves = (nodes, trail = [], parentKey = '', out = []) => {
    (nodes || []).forEach((node, index) => {
        const key = nodeKey(parentKey, node, index);
        const children = nodeChildren(node);
        if (children) {
            collectTreeLeaves(children, [...trail, node.name], key, out);
            return;
        }
        out.push({
            ...node,
            __key: key,
            __level: 0,
            __hasChildren: false,
            __path: trail.filter(Boolean).join(' → '),
            __search: `${trail.join(' ')} ${node.name ?? ''}`.toLowerCase(),
        });
    });
    return out;
};

// Ключи предков узла с заданным value — чтобы при открытии раскрыть путь к выбранному
const findTreeKeyPath = (nodes, value, parentKey = '') => {
    for (let index = 0; index < (nodes || []).length; index += 1) {
        const node = nodes[index];
        const key = nodeKey(parentKey, node, index);
        const children = nodeChildren(node);
        if (!children) {
            if (node.value == value) return [];
            continue;
        }
        const inner = findTreeKeyPath(children, value, key);
        if (inner) return [key, ...inner];
    }
    return null;
};

/**
 * CustomSelect - переиспользуемый компонент выпадающего списка с поиском.
 *
 * Dropdown рендерится через portal в document.body (position: fixed),
 * чтобы не обрезаться overflow модалок/карточек и всегда быть поверх окон.
 *
 * @param {Array} options - массив опций [{name: string, value: any}]
 * @param {any} value - текущее выбранное значение
 * @param {string} title - заголовок/label над селектом
 * @param {number|string} width - ширина компонента (число в px или строка '100%')
 * @param {function} setFieldValue - функция для установки значения (Formik-совместимая)
 * @param {string} valuesName - имя поля для setFieldValue
 * @param {boolean} disabled - заблокирован ли селект (default: false)
 * @param {string} error - текст ошибки валидации
 * @param {string} className - дополнительный CSS класс для кастомизации извне
 * @param {Array} options — плоский список [{name, value}] либо дерево: узел с непустым
 *                          `children` становится раскрывающейся группой (сам не выбирается),
 *                          выбирается только лист. Режим включается автоматически, когда хотя бы
 *                          у одной опции есть `children`. Поиск в дереве ищет по листьям, включая
 *                          названия предков, и показывает их плоским списком с путём
 * @param {boolean} withoutAll - убрать авто-опцию "все" (default: false)
 * @param {string} allLabel - текст авто-опции "все" (default: 'Все'). Используй 'Выбрать' когда список — это выбор конкретного значения, а не фильтр
 * @param {string} placeholder - текст плейсхолдера (default: 'Выбрать')
 * @param {boolean} searchable - включить поле поиска (default: true)
 * @param {number} maxRendered - максимум одновременно отрисованных опций (default: 0 — рисовать все).
 *                               Виртуализации нет, поэтому справочники в тысячи позиций (МКБ-10) вешают
 *                               открытие списка. Поиск при этом работает по всему массиву опций.
 *                               В режиме дерева кап применяется только к результатам поиска
 * @param {boolean} required - пометить поле звёздочкой «*» (обязательность проверяет форма, не сам селект)
 * @param {boolean} floatMessage - выводить ошибку/подсказку абсолютом под полем, не увеличивая высоту контейнера (default: false).
 *                                 Нужен в рядах фильтров, чтобы появление ошибки не смещало само поле и соседние
 * @param {function} renderOption - кастомная функция рендеринга опции (option) => ReactNode
 * @param {function} renderSelected - кастомная функция рендеринга выбранного значения (option) => ReactNode
 * @param {boolean} dropUp - открывать список вверх (редко нужен после portal; оставлен для совместимости)
 */
const CustomSelect = memo(({
    options = [],
    value,
    title,
    width = 200,
    setFieldValue,
    valuesName,
    disabled = false,
    error,
    className,
    withoutAll = false,
    allLabel = 'Все',
    placeholder = 'Выбрать',
    searchable = true,
    maxRendered = 0,
    renderOption,
    renderSelected,
    dropUp = false,
    highlight = false,
    hint,
    required = false,
    floatMessage = false,
    onMenuOpen,
}) => {
    // === STATE ===
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [menuStyle, setMenuStyle] = useState(null);
    // раскрытые группы дерева (ключи узлов); пересобирается при каждом открытии списка
    const [expandedKeys, setExpandedKeys] = useState(() => new Set());

    // === REFS ===
    const selectRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // === MEMOIZED DATA ===
    const data = useMemo(() => {
        if (!options || options.length === 0) return [];
        return withoutAll ? options : [{ name: allLabel, value: '' }, ...options];
    }, [options, withoutAll, allLabel]);

    // Дерево включается самим содержимым options — отдельного пропа нет
    const isTree = useMemo(() => (options || []).some(option => !!nodeChildren(option)), [options]);

    const treeRows = useMemo(
        () => (isTree ? buildTreeRows(data, expandedKeys) : null),
        [isTree, data, expandedKeys]
    );

    const treeLeaves = useMemo(
        () => (isTree ? collectTreeLeaves(data) : null),
        [isTree, data]
    );

    const filteredOptions = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (isTree) {
            if (!query) return treeRows;
            // в поиске дерево «схлопывается»: показываем только листья, зато весь справочник
            return treeLeaves.filter(leaf => leaf.__search.includes(query));
        }
        if (!query) return data;
        return data.filter(option => option.name?.toLowerCase().includes(query));
    }, [data, searchQuery, isTree, treeRows, treeLeaves]);

    // Отрисовываем не больше maxRendered — фильтрация выше идёт по полному массиву,
    // так что до «спрятанных» опций пользователь доходит поиском. В дереве без поиска
    // кап не нужен: на экране только раскрытая ветка, а обрезка разорвала бы уровни.
    const visibleOptions = useMemo(() => {
        const capped = maxRendered > 0 && !(isTree && !searchQuery.trim());
        return capped ? filteredOptions.slice(0, maxRendered) : filteredOptions;
    }, [filteredOptions, maxRendered, isTree, searchQuery]);
    const truncatedCount = filteredOptions.length - visibleOptions.length;

    // В дереве выбираются только листья — по ним же резолвится подпись поля
    const lookupSource = isTree ? treeLeaves : data;

    const selectedDisplayName = useMemo(() => {
        if (value === undefined || value === null || value === '') {
            return withoutAll ? '' : allLabel;
        }
        const found = lookupSource?.find(item => item.value == value);
        return found?.name || '';
    }, [value, lookupSource, withoutAll, allLabel]);

    const updateMenuPosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const gap = 4;
        const maxMenuHeight = 260; // search + optionsList max-height ~200 + padding

        let openUp = dropUp;
        if (!dropUp) {
            const spaceBelow = window.innerHeight - rect.bottom - gap;
            const spaceAbove = rect.top - gap;
            // Prefer down; flip only if снизу мало места, а сверху заметно больше
            if (spaceBelow < Math.min(maxMenuHeight, 160) && spaceAbove > spaceBelow) {
                openUp = true;
            }
        }

        const style = {
            position: 'fixed',
            left: rect.left,
            width: Math.max(rect.width, 120),
            zIndex: 1000100,
        };

        if (openUp) {
            style.bottom = window.innerHeight - rect.top + gap;
            style.top = 'auto';
            style.maxHeight = Math.min(maxMenuHeight, Math.max(120, rect.top - gap - 8));
        } else {
            style.top = rect.bottom + gap;
            style.bottom = 'auto';
            style.maxHeight = Math.min(maxMenuHeight, Math.max(120, window.innerHeight - rect.bottom - gap - 8));
        }

        setMenuStyle(style);
    }, [dropUp]);

    // === HANDLERS ===
    const handleOpen = useCallback(() => {
        if (!disabled) {
            if (onMenuOpen) onMenuOpen();
            setIsOpen(true);
            setHighlightedIndex(-1);
            if (isTree) {
                // раскрываем путь к уже выбранному листу, иначе — только верхний уровень
                const path = (value !== undefined && value !== null && value !== '')
                    ? findTreeKeyPath(data, value)
                    : null;
                setExpandedKeys(new Set(path || []));
            }
            // позиция до paint, чтобы не было «прыжка»
            requestAnimationFrame(() => {
                updateMenuPosition();
                if (inputRef.current) inputRef.current.focus();
            });
        }
    }, [disabled, updateMenuPosition, isTree, data, value, onMenuOpen]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        setMenuStyle(null);
    }, []);

    const toggleTreeNode = useCallback((option) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(option.__key)) next.delete(option.__key);
            else next.add(option.__key);
            return next;
        });
    }, []);

    const handleToggle = useCallback(() => {
        if (isOpen) {
            handleClose();
        } else {
            handleOpen();
        }
    }, [isOpen, handleOpen, handleClose]);

    const handleSelect = useCallback((option) => {
        // группа дерева — не значение: клик разворачивает/сворачивает уровень, список остаётся открытым
        if (option?.__hasChildren) {
            toggleTreeNode(option);
            return;
        }
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        setMenuStyle(null);
        if (setFieldValue && valuesName) {
            setFieldValue(valuesName, option.value);
        }
    }, [setFieldValue, valuesName, toggleTreeNode]);

    const handleSearchChange = useCallback((e) => {
        const newQuery = e.target.value;
        setSearchQuery(newQuery);
        setHighlightedIndex(-1);
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (!isOpen) {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
                e.preventDefault();
                handleOpen();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < visibleOptions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : visibleOptions.length - 1
                );
                break;
            case 'ArrowRight': {
                const option = visibleOptions[highlightedIndex];
                if (option?.__hasChildren && !option.__expanded) {
                    e.preventDefault();
                    toggleTreeNode(option);
                }
                break;
            }
            case 'ArrowLeft': {
                const option = visibleOptions[highlightedIndex];
                if (option?.__hasChildren && option.__expanded) {
                    e.preventDefault();
                    toggleTreeNode(option);
                }
                break;
            }
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && visibleOptions[highlightedIndex]) {
                    handleSelect(visibleOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                handleClose();
                break;
            case 'Tab':
                handleClose();
                break;
            default:
                break;
        }
    }, [isOpen, highlightedIndex, visibleOptions, handleOpen, handleClose, handleSelect, toggleTreeNode]);

    // === EFFECTS ===
    // Click outside: закрыть только dropdown. Тот же жест не должен закрывать
    // родительскую react-responsive-modal.
    // Важно: onClose вешается на .react-responsive-modal-container (не на .overlay) —
    // см. node_modules/react-responsive-modal/src/index.tsx handleClickOverlay.
    useEffect(() => {
        if (!isOpen) return undefined;

        const isInsideSelect = (t) =>
            !!(selectRef.current?.contains(t) || dropdownRef.current?.contains(t));

        // Клик по затемнению = container (fullscreen), не dialog (.modal)
        const isOutsideModalDialog = (t) => {
            if (!t || !t.closest) return false;
            if (t.classList?.contains('react-responsive-modal-overlay')) return true;
            if (t.classList?.contains('react-responsive-modal-container')) return true;
            const inContainer = t.closest('.react-responsive-modal-container');
            const inDialog = t.closest('.react-responsive-modal-modal');
            return !!(inContainer && !inDialog);
        };

        const handleOutsideMouseDown = (event) => {
            if (isInsideSelect(event.target)) return;

            const blockModalClose = isOutsideModalDialog(event.target);
            handleClose();

            // Жест по области вне белой модалки: не даём container.onClick → onClose.
            // Блокировщик click НЕ снимаем в cleanup эффекта — иначе при isOpen→false
            // он исчезнет до события click того же жеста.
            if (blockModalClose) {
                event.stopPropagation();
                event.preventDefault();

                const blockClick = (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    document.removeEventListener('click', blockClick, true);
                };
                document.addEventListener('click', blockClick, true);
                window.setTimeout(() => {
                    document.removeEventListener('click', blockClick, true);
                }, 500);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                event.preventDefault();
                handleClose();
            }
        };

        // capture: до React onClick на .react-responsive-modal-container
        document.addEventListener('mousedown', handleOutsideMouseDown, true);
        document.addEventListener('keydown', handleEscape, true);

        return () => {
            document.removeEventListener('mousedown', handleOutsideMouseDown, true);
            document.removeEventListener('keydown', handleEscape, true);
        };
    }, [isOpen, handleClose]);

    // Скролл закрывает список, resize — пересчитывает позицию.
    // Меню в portal с position: fixed, поэтому при прокрутке страницы или тела модалки
    // поле уезжает, а список остаётся висеть у viewport. Гонять пересчёт на каждом
    // событии scroll дорого (пачки событий → setState на кадр) и всё равно выглядит
    // «оторванно», когда trigger уходит под шапку окна. Закрываем — как и нативный
    // список: жест прокрутки относится к странице, а не к выбору значения.
    useEffect(() => {
        if (!isOpen) return undefined;

        updateMenuPosition();

        let frame = 0;
        const scheduleUpdate = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                updateMenuPosition();
            });
        };

        // scroll не всплывает — слушаем в capture, event.target = реально прокрученный узел.
        // Прокрутка самого списка опций (в т.ч. scrollIntoView при навигации стрелками)
        // приходит сюда же и закрывать список не должна.
        const handleScroll = (event) => {
            const target = event.target;
            if (target && target.nodeType === 1 && dropdownRef.current?.contains(target)) return;
            handleClose();
        };

        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            if (frame) cancelAnimationFrame(frame);
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen, updateMenuPosition, handleClose]);

    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const items = listRef.current.querySelectorAll('li');
            const item = items[highlightedIndex];
            if (item) item.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex]);

    // Сброс подсветки при смене состава списка. В дереве длина меняется ещё и от
    // раскрытия уровня — там позицию пользователя терять не надо, ориентир только на поиск.
    const highlightResetKey = isTree ? searchQuery : visibleOptions.length;
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [highlightResetKey]);

    // === STYLES ===
    const computedWidth = typeof width === 'number' ? width + 20 : width;

    const containerClass = cn(styles.container, className);
    const selectBoxClass = cn(styles.selectBox, {
        [styles.selectBoxOpen]: isOpen,
        [styles.selectBoxDisabled]: disabled,
        [styles.selectBoxError]: error,
    });

    const getOptionClass = (index, option) => cn(styles.option, {
        [styles.optionGroup]: option.__hasChildren,
        [styles.optionHighlighted]: index === highlightedIndex,
        [styles.optionSelected]: !option.__hasChildren
            && value !== null && value !== undefined && option.value == value,
    });

    const renderTreeOption = (option) => (
        <span
            className={styles.treeRow}
            style={option.__level ? { paddingLeft: option.__level * TREE_INDENT } : undefined}
        >
            <span className={styles.treeChevron}>
                {option.__hasChildren && (option.__expanded ? <IoIosArrowDown /> : <IoIosArrowForward />)}
            </span>
            <span>
                {option.name}
                {!!option.__path && <span className={styles.treePath}>{option.__path}</span>}
            </span>
        </span>
    );

    const dropdownNode = isOpen && !disabled && menuStyle && createPortal(
        <div
            ref={dropdownRef}
            className={styles.dropdownPortal}
            style={menuStyle}
        >
            {searchable && options.length > 5 && (
                <div className={styles.searchWrapper}>
                    <input
                        ref={inputRef}
                        type="text"
                        className={styles.searchInput}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Поиск..."
                        autoComplete="off"
                    />
                </div>
            )}

            <ul ref={listRef} className={styles.optionsList} role="listbox">
                {visibleOptions.length > 0 ? (
                    visibleOptions.map((option, index) => (
                        <li
                            key={`${option.__key ?? option.value}-${valuesName}-${index}`}
                            className={getOptionClass(index, option)}
                            onClick={() => handleSelect(option)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            role={option.__hasChildren ? 'treeitem' : 'option'}
                            aria-expanded={option.__hasChildren ? !!option.__expanded : undefined}
                            aria-selected={!option.__hasChildren
                                && value !== null && value !== undefined && option.value == value}
                        >
                            {renderOption
                                ? renderOption(option)
                                : (isTree ? renderTreeOption(option) : option.name)}
                        </li>
                    ))
                ) : (
                    <li className={styles.noResults}>Ничего не найдено</li>
                )}
                {truncatedCount > 0 && (
                    <li className={styles.moreHint}>
                        Показаны первые {visibleOptions.length} из {filteredOptions.length} — уточните поиск
                    </li>
                )}
            </ul>
        </div>,
        document.body
    );

    // === RENDER ===
    return (
        <div className={containerClass} ref={selectRef} style={{ width: computedWidth }}>
            {title && (
                <label className={cn(styles.label, { [styles.greenLabel]: highlight })}>
                    {title}{required && <span className={styles.requiredMark}> *</span>}
                </label>
            )}

            <div
                ref={triggerRef}
                className={selectBoxClass}
                onClick={!disabled ? handleToggle : undefined}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-disabled={disabled}
                aria-required={required || undefined}
            >
                <div className={styles.selectedText}>
                    {renderSelected
                        ? (value !== undefined && value !== null && value !== ''
                            ? renderSelected(lookupSource?.find(item => item.value == value))
                            : placeholder)
                        : (selectedDisplayName || placeholder)
                    }
                </div>
                <div className={styles.arrow}>
                    {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </div>
            </div>

            {dropdownNode}

            {!!error && <span className={cn(styles.errorText, { [styles.floatMessage]: floatMessage })}>{error}</span>}
            {!error && hint && (
                <span className={cn(styles.hintText, { [styles.floatMessage]: floatMessage })}>
                    <IoWarningOutline />{hint}
                </span>
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.value === nextProps.value &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.error === nextProps.error &&
        prevProps.options === nextProps.options &&
        prevProps.title === nextProps.title &&
        prevProps.width === nextProps.width &&
        prevProps.className === nextProps.className &&
        prevProps.withoutAll === nextProps.withoutAll &&
        prevProps.allLabel === nextProps.allLabel &&
        prevProps.valuesName === nextProps.valuesName &&
        prevProps.searchable === nextProps.searchable &&
        prevProps.maxRendered === nextProps.maxRendered &&
        prevProps.placeholder === nextProps.placeholder &&
        prevProps.renderOption === nextProps.renderOption &&
        prevProps.renderSelected === nextProps.renderSelected &&
        prevProps.dropUp === nextProps.dropUp &&
        prevProps.highlight === nextProps.highlight &&
        prevProps.hint === nextProps.hint &&
        prevProps.required === nextProps.required &&
        prevProps.floatMessage === nextProps.floatMessage &&
        prevProps.onMenuOpen === nextProps.onMenuOpen
    );
});

CustomSelect.displayName = 'CustomSelect';

export default CustomSelect;
